import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Bell, BellRing, Search, Archive, ArchiveRestore, Megaphone, Send, ChevronDown, CornerDownRight, PenSquare } from "lucide-react";
import { Reminders } from "@/components/Bookings";
import { formatApiError, useAuth } from "@/auth/AuthContext";
import { KioskRemindersCard } from "@/components/PlatformCards";
import { StaffNoticesCard } from "@/components/StaffNotices";
import { PageHeader, SubTabs } from "@/components/PageChrome";
import { isDayProgramme, kindShort } from "@/lib/programmeKinds";
import RichEmailEditor from "@/components/RichEmailEditor";
import RichText, { htmlHasText } from "@/components/RichText";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const inputCls = "w-full rounded-lg border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] px-2.5 py-1.5 text-xs text-[var(--hp-heading)] outline-none focus:border-[var(--hp-primary)] transition-colors";
const labelCls = "text-xs uppercase tracking-wider text-[var(--hp-muted)] mb-1 block";

function ReplyBox({ api, messageId, onSent }) {
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = htmlHasText(html);
  const send = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await axios.post(`${api}/admin-messages/${messageId}/reply`, { html });
      setHtml("");
      toast.success("Reply sent");
      onSent?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-2" data-testid={`message-reply-box-${messageId}`}>
      <RichEmailEditor api={api} value={html} onChange={setHtml} testId={`message-reply-text-${messageId}`} minHeight="4.5rem" />
      <button data-testid={`message-reply-send-${messageId}`} disabled={busy || !ready} onClick={send}
        className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hp-primary)] px-3 py-1.5 text-xs font-bold text-[#04231a] disabled:opacity-40 shrink-0">
        <Send className="h-3 w-3" /> Reply
      </button>
    </div>
  );
}

// Start a new conversation with a family (the feed below only lets you answer messages they sent first)
function NewMessageCard({ api, onSent }) {
  const [open, setOpen] = useState(false);
  const [parents, setParents] = useState([]);
  const [q, setQ] = useState("");
  const [parent, setParent] = useState(null);
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && parents.length === 0) axios.get(`${api}/parents`).then((r) => setParents(r.data)).catch(() => {}); }, [open, api, parents.length]);
  const needle = q.trim().toLowerCase();
  const matches = needle.length >= 1 && !parent ? parents.filter((p) => `${p.name} ${p.email}`.toLowerCase().includes(needle)).slice(0, 8) : [];
  const send = async () => {
    if (!parent || !htmlHasText(html)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${api}/admin-messages/new`, { parent_user_id: parent.user_id, html });
      toast.success(r.data.emailed ? `Message sent to ${parent.name} — emailed too` : `Message sent to ${parent.name}'s parent portal${parent.email ? " (email not sent — connect Gmail in Settings)" : ""}`);
      setHtml(""); setParent(null); setQ(""); setOpen(false); onSent?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!open) return <button data-testid="new-message-open" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hp-primary)] px-4 py-2 text-xs font-bold text-[#04231a]"><PenSquare className="h-3.5 w-3.5" /> New message</button>;
  return (
    <div className="rounded-2xl border border-[var(--hp-primary)]/40 bg-[var(--hp-card)] p-5 space-y-3" data-testid="new-message-card">
      <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)] inline-flex items-center gap-2"><PenSquare className="h-4 w-4 text-[var(--hp-accent)]" /> New message to a family</h3>
      <div className="relative">
        <label className={labelCls}>To</label>
        <input data-testid="new-message-to" value={q} onChange={(e) => { setQ(e.target.value); setParent(null); }} placeholder="Start typing a parent's name or email…" className={inputCls} />
        {matches.length > 0 && <div className="absolute z-20 left-0 right-0 mt-1 rounded-xl border border-[var(--hp-border-hi)] bg-[var(--hp-card)] shadow-2xl p-1" data-testid="new-message-matches">{matches.map((p) => (
          <button key={p.user_id} data-testid={`new-message-pick-${p.user_id}`} onClick={() => { setParent(p); setQ(p.name); }} className="w-full text-left rounded-lg px-3 py-1.5 text-xs text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]">{p.name} <span className="text-[var(--hp-muted)]">· {p.email}</span></button>))}</div>}
      </div>
      <div><label className={labelCls}>Message</label><RichEmailEditor api={api} value={html} onChange={setHtml} testId="new-message-text" minHeight="7rem" /></div>
      <p className="text-xs text-[var(--hp-muted)]">It appears in their parent portal under "Message the office" and is emailed to them. Their answer comes back here as a parent message.</p>
      <div className="flex gap-2">
        <button data-testid="new-message-send" disabled={busy || !parent || !htmlHasText(html)} onClick={send} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hp-primary)] px-4 py-2 text-xs font-bold text-[#04231a] disabled:opacity-40"><Send className="h-3.5 w-3.5" /> Send</button>
        <button data-testid="new-message-cancel" onClick={() => setOpen(false)} className="rounded-full border border-[var(--hp-border-hi)] px-4 py-2 text-xs font-semibold text-[var(--hp-heading)]">Cancel</button>
      </div>
    </div>
  );
}

function MessagesFeed({ api, onRead, category, heading, description, emptyLabel, showReply }) {
  const [rows, setRows] = useState(null);
  const [unreadIds, setUnreadIds] = useState(new Set());
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(() => {
    axios.get(`${api}/admin-messages`, { params: { archived: showArchived, q: q.trim() || undefined, category } }).then(async (r) => {
      setRows(r.data.messages);
      const unread = r.data.messages.filter((m) => !m.read).map((m) => m.id);
      setUnreadIds(new Set(unread));
      if (unread.length && !showArchived && !q.trim()) {
        await axios.post(`${api}/admin-messages/mark-read`, { ids: unread }).catch(() => {});
        onRead?.();
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, showArchived, q, category]);

  useEffect(() => {
    let cancelled = false;
    const id = setTimeout(() => { if (!cancelled) load(); }, 250);
    return () => { cancelled = true; clearTimeout(id); };
  }, [load]);

  const archive = async (id, toArchived) => {
    try { await axios.post(`${api}/admin-messages/${toArchived ? "archive" : "unarchive"}`, { ids: [id] }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!rows) return null;
  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 space-y-3" data-testid={`messages-feed-${category}`}>
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)] inline-flex items-center gap-2">{heading}</h3>
        <span className="text-xs text-[var(--hp-muted)]" data-testid="admin-messages-count">{rows.length} message{rows.length === 1 ? "" : "s"}</span>
        <button data-testid="admin-messages-toggle-archived" onClick={() => setShowArchived((s) => !s)}
          className={`ml-auto inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${showArchived ? "border-[var(--hp-primary)] bg-[var(--hp-primary)]/15 text-[var(--hp-profit)]" : "border-[var(--hp-border-hi)] text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]"}`}>
          {showArchived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />} {showArchived ? "Viewing archived" : "View archived"}
        </button>
      </div>
      <p className="text-xs text-[var(--hp-muted)]">{description}</p>
      <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--hp-muted)]" />
        <input data-testid="admin-messages-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by parent, family or child…" className={`${inputCls} pl-8`} /></div>
      {rows.length === 0 ? (
        <p className="text-sm text-[var(--hp-muted)]" data-testid="admin-messages-empty">{showArchived ? "No archived messages." : q.trim() ? "No messages match your search." : emptyLabel}</p>
      ) : (
        <div className="space-y-2">{rows.map((m) => (
          <div key={m.id} data-testid={`admin-message-${m.id}`} className={`rounded-xl border px-4 py-3 text-sm ${unreadIds.has(m.id) ? "border-[var(--hp-accent)]/50 bg-[var(--hp-accent)]/5" : "border-[var(--hp-border)]"}`}>
            <div className="flex items-start gap-2.5">
              {unreadIds.has(m.id) && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[var(--hp-accent)]" data-testid={`admin-message-new-${m.id}`} title="New" />}
              <div className="min-w-0 flex-1">
                <p className="text-[var(--hp-heading)]">{m.summary}</p>
                {(m.parentName || m.childName) && <p className="text-xs text-[var(--hp-muted)] mt-0.5">{[m.parentName, m.childName].filter(Boolean).join(" · ")}</p>}
                {m.detail && <div className="text-xs text-[var(--hp-muted)] mt-0.5">{showReply ? "" : "Reason: "}<RichText html={m.detailHtml} text={m.detail} className="inline-block align-top" /></div>}
                <p className="text-xs text-[var(--hp-muted)] mt-1">{fmtWhen(m.created_at)}</p>
              </div>
              <button data-testid={`admin-message-${showArchived ? "unarchive" : "archive"}-${m.id}`} onClick={() => archive(m.id, !showArchived)} title={showArchived ? "Restore" : "Archive"}
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[var(--hp-muted)] hover:text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]">
                {showArchived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
              </button>
            </div>
            {showReply && (m.replies || []).length > 0 && (
              <div className="mt-2 ml-4 pl-3 border-l-2 border-[var(--hp-border)] space-y-1.5" data-testid={`message-replies-${m.id}`}>
                {m.replies.map((rep, i) => (
                  <p key={i} className="text-xs text-[var(--hp-text)] flex items-start gap-1.5"><CornerDownRight className="h-3 w-3 mt-0.5 shrink-0 text-[var(--hp-muted)]" />
                    <span className="min-w-0"><span className="font-semibold text-[var(--hp-heading)]">{rep.by}:</span> <RichText html={rep.html} text={rep.text} className="inline-block align-top" /> <span className="text-xs text-[var(--hp-muted)]">· {fmtWhen(rep.at)}</span></span>
                  </p>
                ))}
              </div>
            )}
            {showReply && !showArchived && <ReplyBox api={api} messageId={m.id} onSent={load} />}
          </div>
        ))}</div>
      )}
    </div>
  );
}

function ActivitiesDropdown({ activities, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const label = selected.length === 0 ? "All activities" : selected.length === 1 ? "1 activity selected" : `${selected.length} activities selected`;
  return (
    <div className="relative" data-testid="comms-activities-dropdown">
      <button type="button" data-testid="comms-activities-toggle" onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-full border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] px-3.5 py-1.5 text-xs font-semibold text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]">
        {label} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1.5 z-20 w-80 max-h-72 overflow-y-auto rounded-xl border border-[var(--hp-border-hi)] bg-[var(--hp-card)] shadow-2xl p-1.5" data-testid="comms-activities-panel">
          {activities.length === 0 && <p className="px-3 py-2 text-xs text-[var(--hp-muted)]">No activities yet — create terms or holiday programmes on Activities → Programmes.</p>}
          {activities.map((a) => (
            <label key={a.id} data-testid={`comms-activity-${a.id}`} className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)] cursor-pointer">
              <input type="checkbox" checked={selected.includes(a.id)} onChange={() => onToggle(a.id)} className="h-3.5 w-3.5 accent-[var(--hp-primary)]" />
              <span className="flex-1">{a.name}</span>
              <span className={`rounded-full border px-2 py-0.5 text-xs uppercase ${isDayProgramme(a.kind) ? "border-[var(--hp-gold)]/40 text-[var(--hp-gold)]" : "border-[var(--hp-accent)]/40 text-[var(--hp-accent)]"}`}>{kindShort(a.kind)}</span>
            </label>
          ))}
          {selected.length > 0 && <button data-testid="comms-activities-clear" onClick={onClear} className="w-full text-left px-3 py-1.5 text-xs text-[var(--hp-muted)] hover:text-[var(--hp-heading)] underline">Clear selection</button>}
        </div>
      )}
    </div>
  );
}

function ParentCommunicationCard({ api }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [activities, setActivities] = useState([]);
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${api}/programmes`, { params: { include_archived: true } }).then((r) => setActivities(r.data)).catch(() => {}); }, [api]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  useEffect(() => {
    const id = setTimeout(() => {
      axios.post(`${api}/communications/preview`, { programme_ids: selected.length ? selected : null })
        .then((r) => setPreview(r.data.families)).catch(() => setPreview(null));
    }, 300);
    return () => clearTimeout(id);
  }, [api, selected]);

  const send = async () => {
    if (!subject.trim() || !htmlHasText(message)) { toast.error("Write a subject and a message"); return; }
    const count = preview ?? 0;
    if (!window.confirm(`Send this email to ${count} famil${count === 1 ? "y" : "ies"}?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${api}/communications/send`, { subject: subject.trim(), html: message, programme_ids: selected.length ? selected : null });
      toast[r.data.sent ? "success" : "warning"](`${r.data.sent} emailed${r.data.skipped ? ` · ${r.data.skipped} skipped (connect Gmail in Settings)` : ""} of ${r.data.families} families`);
      setSubject(""); setMessage("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 space-y-3" data-testid="parent-communication-card">
      <h3 className="font-head text-lg font-semibold text-[var(--hp-heading)] inline-flex items-center gap-2"><Megaphone className="h-4 w-4 text-[var(--hp-gold)]" /> Send a message to parents</h3>
      <p className="text-xs text-[var(--hp-muted)]">Compose a one-off email — newsletters, promotions, holiday programme announcements. Make text bold or coloured and add links or pictures. Pick one or more activities to reach only families attending those, or leave none selected to email all families.</p>
      <ActivitiesDropdown activities={activities} selected={selected} onToggle={toggle} onClear={() => setSelected([])} />
      <div><label className={labelCls}>Subject</label>
        <input data-testid="comms-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} placeholder="e.g. Holiday programme bookings now open" /></div>
      <div><label className={labelCls}>Message</label>
        <RichEmailEditor api={api} value={message} onChange={setMessage} testId="comms-message" minHeight="9rem" /></div>
      <div className="flex items-center gap-3">
        <button data-testid="comms-send" onClick={send} disabled={busy} className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hp-primary)] px-4 py-2 text-xs font-bold text-[#04231a] disabled:opacity-40"><Send className="h-3.5 w-3.5" /> Send</button>
        <span className="text-xs text-[var(--hp-muted)]" data-testid="comms-preview-count">{preview === null ? "" : `${preview} famil${preview === 1 ? "y" : "ies"} will receive this`}</span>
      </div>
    </div>
  );
}

export const TABS = [["bookings", "Booking changes", Bell], ["parent", "Parent messages", MessageSquare], ["reminders", "Reminders & notices", BellRing]];

export default function Messages({ api, onRead, initialTab }) {
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const [tab, setTab] = useState(initialTab || "bookings");
  const [feedKey, setFeedKey] = useState(0);
  const [counts, setCounts] = useState({});
  const loadCounts = useCallback(() => axios.get(`${api}/admin-messages/unread-count`).then((r) => setCounts(r.data)).catch(() => {}), [api]);
  useEffect(() => { loadCounts(); const id = setInterval(loadCounts, 30000); return () => clearInterval(id); }, [loadCounts]);
  // Open on whichever tab has unread messages (parent messages need a reply; booking-change notices are only FYI)
  const autoPicked = useRef(false);
  useEffect(() => {
    if (autoPicked.current || initialTab || counts.unread === undefined) return;
    autoPicked.current = true;
    if (!counts.bookings && counts.parent) setTab("parent");
  }, [counts, initialTab]);
  // Viewing a tab marks its messages read: refresh this tab's counts as well as the sidebar badge
  const handleRead = () => { loadCounts(); onRead?.(); };
  return (
    <div className="space-y-6" data-testid="messages">
      <PageHeader eyebrow="Messages" title="Messages" testId="messages-header">
        <SubTabs tabs={TABS.map(([id, label, icon]) => ({ id, label, icon, badge: counts[id], badgeTestId: `messages-subtab-unread-${id}` }))} value={tab} onChange={setTab} testId="messages-subtabs" itemTestId={(k) => `messages-subtab-${k}`} />
      </PageHeader>
      {tab === "bookings" && <MessagesFeed api={api} onRead={handleRead} category="bookings"
        heading={<><Bell className="h-4 w-4 text-[var(--hp-accent)]" /> Booking changes</>}
        description="Automatic notices — a parent cancelling, rescheduling or marking a child absent, medication given to a child at the programme, plus housekeeping alerts like a family becoming eligible for data deletion."
        emptyLabel="No booking-change notices yet." showReply={false} />}
      {tab === "parent" && <NewMessageCard api={api} onSent={() => setFeedKey((k) => k + 1)} />}
      {tab === "parent" && <MessagesFeed key={feedKey} api={api} onRead={handleRead} category="parent"
        heading={<><MessageSquare className="h-4 w-4 text-[var(--hp-accent)]" /> Parent messages</>}
        description="Messages parents have sent from their dashboard, and messages you've started with them. Reply here — it's emailed straight back to them."
        emptyLabel="No messages from parents yet." showReply={true} />}
      {tab === "reminders" && <div className="space-y-6" data-testid="messages-reminders">
        <p className="text-sm text-[var(--hp-muted)] max-w-3xl">Everything you want parents or staff to see or be reminded of, in one place: emails to families, pop-ups on the sign-in kiosk, and the staff notice board.</p>
        <Reminders api={api} />
        <ParentCommunicationCard api={api} />
        <KioskRemindersCard api={api} isAdmin={isAdmin} />
        <StaffNoticesCard api={api} />
      </div>}
    </div>
  );
}
